import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { fetchAllPermissions, createRole } from 'actions/roleActions';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { startLoading } from 'reducers/loading/loading.action';
import EditRolePermissions from 'components/Roles/EditRolePermissions';
import { changeGroupNameToOrganization } from './RolesUtils/groupToOrgTransformations';

export default function NewRoleDialog({ show, group_guid, onClose }) {
  const reduxDispatch = useDispatch();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid, isSubmitted },
  } = useForm({ mode: 'onChange' });

  const [isLoaded, setIsLoaded] = useState(true);

  const { user_guid } = useSelector((state) => {
    return state.app.user;
  });

  useEffect(() => {
    if (typeof user_guid !== 'undefined') {
      reduxDispatch(fetchAllPermissions());
    }
  }, [user_guid, reduxDispatch]);

  const isCreateRoleLoaded = useSelector((state) => {
    return state.app.isCreateRoleLoaded;
  });

  const isFetchAllOrgScopedRolesInGroupLoaded = useSelector((state) => {
    return state.app.isFetchAllOrgScopedRolesInGroupLoaded;
  });

  useEffect(() => {
    if (
      !isLoaded &&
      isCreateRoleLoaded &&
      isFetchAllOrgScopedRolesInGroupLoaded
    ) {
      onClose();
    }
  }, [
    isCreateRoleLoaded,
    isFetchAllOrgScopedRolesInGroupLoaded,
    isLoaded,
    onClose,
  ]);

  const reduxAllPermissions = useSelector((state) => {
    const permissions =
      Array.isArray(state.app.allPermissions) &&
      state.app.allPermissions
        .filter((perm) => {
          if (perm.action === 'Open or Close Group') {
            return false;
          }

          return true;
        })
        .map((permission) => {
          switch (permission.action) {
            case 'Open or Close Incident':
              return {
                ...permission,
                title: 'Archive Incident',
                description:
                  'Allows the user to end the activity of a Incident. This locks out all Incident members from performing any action within the Incident or accessing Incident data.',
              };
            default:
              return {
                ...permission,
                title: changeGroupNameToOrganization(permission.title),
                description: changeGroupNameToOrganization(
                  permission.description
                ),
              };
          }
        });

    return permissions || [];
  });

  const [rolePermissions, setRolePermissions] = useState([]);

  if (typeof reduxAllPermissions === 'undefined') {
    return <div>Loading New Role Dialog....</div>;
  }

  const onNewRoleCreateSubmit = ({ roleName, roleDescription }) => {
    setIsLoaded(false);

    const role = {};
    role['group_guid'] = group_guid;
    role['role_name'] = roleName;
    role['description'] = roleDescription;
    role['access_level'] = 'PUBLIC'; // We can reintroduce this selection when appropriate
    role['permissions'] = rolePermissions.map((perm) => perm.id);
    reduxDispatch(startLoading());
    reduxDispatch(createRole(role));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
        style={{ '--bs-modal-width': '50vw' }}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>New Role</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onNewRoleCreateSubmit)}>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Role Name:</label>
              <Controller
                control={control}
                name="roleName"
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <StylishNewInput
                    type="text"
                    onChange={(e) => onChange(e.target.value)}
                    value={value || ''}
                  />
                )}
              />
              {errors.roleName && (
                <span className="form-text form-error">
                  This field is required
                </span>
              )}
              {['Org Admin', 'Group Admin'].includes(watch('roleName')) ? (
                <span className="form-text form-error">
                  You cannot create a role with that name
                </span>
              ) : null}
            </div>
            {!['Org Admin', 'Group Admin'].includes(watch('roleName')) && (
              <>
                <div className="mb-3">
                  <label className="form-label">Role Description:</label>
                  <Controller
                    control={control}
                    name="roleDescription"
                    rules={{ required: true, maxLength: 500 }}
                    render={({ field: { onChange, value } }) => (
                      <StylishNewTextArea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    )}
                  />
                  {errors.roleDescription?.type === 'required' && (
                    <span className="form-text form-error">
                      This field is required
                    </span>
                  )}
                  {errors.roleDescription?.type === 'maxLength' && (
                    <span className="form-text form-error">
                      Maximum 500 character allowed
                    </span>
                  )}
                </div>
                <hr className="dashed" />
                <EditRolePermissions
                  currentPermissions={rolePermissions}
                  onPermissionChange={setRolePermissions}
                  permissionsOptions={reduxAllPermissions}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="pt-0">
            <div className="button-group flex-column">
              <StylishNewButton
                type="submit"
                className="button--primary"
                disabled={
                  !isLoaded ||
                  ['Org Admin', 'Group Admin'].includes(watch('roleName')) ||
                  (isSubmitted && !isValid)
                }
              >
                Create New Role
              </StylishNewButton>
              {isSubmitted && !isValid && (
                <span className="form-text form-error mt-4">
                  Form Validation Error
                </span>
              )}
              {!isLoaded && (
                <span className="form-text form-error mt-3">
                  The role is not yet loaded
                </span>
              )}
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
